import React, { useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, Link, Paper, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { usePagesTranslation } from "../locales";
import { ACCENT, SURFACE, TEXT } from "../../dark-theme/palette";
import SocialShareDropdown, {
    buildStorySharePageUrl,
} from "../puzzles/common/components/SocialShareDropdown";
import {
    collectWordObjsForStoryMask,
    MaskedStorySnippet,
    PUZZLE_WORD_UNDERLINE_SX,
} from "./storyCardMask";
import { getAppCopyrightLine } from "../../config/appCopyright";
import { fetchHomeStoryWordPuzzleBundle } from "./storybookGridPreview";
import StorybookLinkedPuzzles from "./StorybookLinkedPuzzles";
import StoryRatingStars from "../components/StoryRatingStars";
import {
    formatHomeStoryPublishedLabel,
    getHomeStoryPuzzleType,
    getLocalizedStoryFromHomeStoryRo,
    homeStoryHasPlayResolutionCandidate,
    pickStoryCoverImageUrl,
    resolveHomeStoryShareId,
    buildReadStoryPath,
    getHomeStoryRowId,
} from "./storyRoUtils";

/** Story detail panel: cover, full text, puzzle-word hints, linked puzzles. */
export default function StorybookReader({
    row,
    contentLanguage,
    loadingDetail,
    /** When false, the site copyright is rendered by the layout (below the ad). */
    showCopyright = true,
    /** `h1` on `/read-story`; `h2` inside Storybook. */
    headingComponent = "h2",
    /** Show a permalink to the indexable article page (Storybook split view). */
    showPermalink = false,
}) {
    const { t } = usePagesTranslation();
    const { title, text, imageUrl } = getLocalizedStoryFromHomeStoryRo(row, contentLanguage);
    const headTitle = title || row?.templateTitle || "—";
    /** Reader shows the full-size square art; the 400px icon is only a fallback. */
    const coverUrl = imageUrl || pickStoryCoverImageUrl(row);
    const publishedLabel = formatHomeStoryPublishedLabel(row);
    const storyText = String(text ?? "").trim();

    const rowWordObjs = useMemo(
        () => (row ? collectWordObjsForStoryMask(row) : []),
        [row]
    );
    const [fetchedWordObjs, setFetchedWordObjs] = useState([]);

    useEffect(() => {
        if (!row || !homeStoryHasPlayResolutionCandidate(row)) {
            setFetchedWordObjs([]);
            return undefined;
        }
        let cancelled = false;
        fetchHomeStoryWordPuzzleBundle(row)
            .then(({ wordObjs }) => {
                if (!cancelled) setFetchedWordObjs(Array.isArray(wordObjs) ? wordObjs : []);
            })
            .catch(() => {
                if (!cancelled) setFetchedWordObjs([]);
            });
        return () => {
            cancelled = true;
        };
    }, [row]);

    const puzzleWordObjs = useMemo(
        () => [...rowWordObjs, ...fetchedWordObjs],
        [rowWordObjs, fetchedWordObjs]
    );

    const storyShareId = useMemo(() => resolveHomeStoryShareId(row), [row]);
    const storyShareUrl = useMemo(
        () => (storyShareId ? buildStorySharePageUrl(storyShareId) : ""),
        [storyShareId]
    );
    const storySharePuzzleType = useMemo(
        () => getHomeStoryPuzzleType(row) || "CROSSWORD",
        [row]
    );
    const readStoryPath = useMemo(
        () => buildReadStoryPath(getHomeStoryRowId(row) || storyShareId),
        [row, storyShareId]
    );
    const copyrightLine =
        getAppCopyrightLine() || String(t("landing.copyright", "")).trim();

    if (loadingDetail) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                boxSizing: "border-box",
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                bgcolor: SURFACE.card,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "inset 0 0 40px rgba(2, 6, 18, 0.35)",
            }}
        >
            {coverUrl ? (
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        aspectRatio: "1 / 1",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: SURFACE.sunken,
                        mb: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={coverUrl}
                        alt={headTitle}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                </Box>
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 2,
                }}
            >
                <Typography
                    variant="h5"
                    component={headingComponent}
                    sx={{ fontWeight: 700, color: TEXT.primary, flex: 1, minWidth: 0 }}
                >
                    {headTitle}
                </Typography>
                {storyShareUrl ? (
                    <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                        <SocialShareDropdown
                            compact
                            puzzleTitle={headTitle}
                            puzzleId={storyShareId}
                            puzzleType={storySharePuzzleType}
                            shareUrl={storyShareUrl}
                            shareMessage={t("puzzle.playCross.story-share-message", {
                                title: headTitle,
                            })}
                            tooltipTitle={t("puzzle.playCross.story-share-tooltip")}
                        />
                    </Box>
                ) : null}
            </Box>
            <StoryRatingStars storyId={getHomeStoryRowId(row) || storyShareId} />
            <MaskedStorySnippet
                text={storyText}
                wordObjs={puzzleWordObjs}
                highlightMode="underline"
                emptyLabel="—"
                component="div"
                variant="body1"
                sx={{
                    color: "text.primary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.75,
                    fontFamily: "Georgia, 'Times New Roman', serif",
                }}
            />
            {publishedLabel ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 2, textAlign: "right" }}
                >
                    {t("landing.puzzle-info-published")}: {publishedLabel}
                </Typography>
            ) : null}
            {showPermalink && readStoryPath ? (
                <Typography variant="body2" sx={{ mt: 1.25 }}>
                    <Link
                        component={RouterLink}
                        to={readStoryPath}
                        sx={{ fontWeight: 600, color: "inherit", textDecoration: "underline" }}
                    >
                        {t("stories.open-full-page", "Open full story page")}
                    </Link>
                </Typography>
            ) : null}
            {puzzleWordObjs.length > 0 && storyText ? (
                <Box
                    sx={{
                        mt: publishedLabel ? 1.5 : 2,
                        mb: 0,
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 1.5,
                        bgcolor: "rgba(59, 166, 102, 0.08)",
                        border: "1px solid",
                        borderColor: "rgba(59, 166, 102, 0.35)",
                        boxShadow: "0 2px 8px rgba(59, 166, 102, 0.12)",
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                        <SearchIcon sx={{ color: ACCENT.light, mt: 0.25, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, color: TEXT.primary, mb: 0.5, lineHeight: 1.35 }}
                            >
                                {t("storybook.puzzle-words-hint-title")}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", lineHeight: 1.6, mb: 1 }}
                            >
                                {t("storybook.puzzle-words-hint-body")}
                            </Typography>
                            <Typography variant="body2" sx={{ color: TEXT.primary, lineHeight: 1.5 }}>
                                {t("storybook.puzzle-words-hint-example-label")}{" "}
                                <Box component="span" sx={PUZZLE_WORD_UNDERLINE_SX}>
                                    treasure
                                </Box>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : null}
            <StorybookLinkedPuzzles row={row} />
        </Paper>
            {showCopyright && copyrightLine ? (
                <Box
                    component="footer"
                    sx={{
                        width: "100%",
                        mt: 3,
                        pt: 2,
                        pb: 1,
                        px: { xs: 2, sm: 3 },
                        borderTop: "1px solid",
                        borderColor: "divider",
                        boxSizing: "border-box",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.5 }}
                    >
                        {copyrightLine}
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
}
